import { useState } from 'react';
import { TutorPanel } from './TutorPanel';
import type { HintChain } from '@curriculum/types';

/**
 * Mobile bottom-sheet wrapper for the TutorPanel. The desktop TutorPanel
 * sticks in the right sidebar (lg:block); this one floats as a pill on
 * smaller screens and opens an overlay sheet. Surfaces the "stuck?" affordance
 * at the bottom-right thumb zone where a one-handed reader can reach it.
 */
export function TutorDrawer({ chapterId, hintChains }: { chapterId: string; hintChains: HintChain[] }) {
  const [open, setOpen] = useState(false);

  if (hintChains.length === 0) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="lg:hidden fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-30 rounded-full bg-accent-500 text-ink-900 text-sm font-semibold px-5 py-3 shadow-btn ring-1 ring-accent-600/20 hover:bg-accent-600 hover:text-white active:translate-y-px transition-[background-color,color,transform] duration-150"
        aria-label="Open tutor hints"
      >
        Stuck? <span className="font-mono">{hintChains.length}</span> hints
      </button>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-night-900/50 backdrop-blur-[2px] flex items-end"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="bg-cream w-full rounded-t-[1.25rem] max-h-[85vh] overflow-y-auto shadow-card-raised animate-fade-up motion-reduce:animate-none"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Tutor hints"
          >
            <div className="sticky top-0 px-5 py-3 border-b border-ink-200/80 bg-cream/95 backdrop-blur flex items-center justify-between z-10">
              <span className="text-xs uppercase tracking-wide text-ink-500 font-semibold">Tutor</span>
              <button
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 text-2xl leading-none transition-colors hover:bg-ink-100 hover:text-ink-900"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-3">
              <TutorPanel chapterId={chapterId} hintChains={hintChains} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
